import type { DatePickerShortcut } from './types';

export const DAYS_FR = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

export const MONTHS_FR_SHORT = [
  'JANV.', 'FÉVR.', 'MARS', 'AVR.', 'MAI', 'JUIN',
  'JUIL.', 'AOÛT', 'SEPT.', 'OCT.', 'NOV.', 'DÉC.',
];

export const MONTHS_FR_FULL = [
  'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre',
];

export const formatDateFR = (date: Date | null): string => {
  if (!date) return '';
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

export const formatTimeFR = (date: Date | null): string => {
  if (!date) return '';
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  return `${hours}:${minutes}`;
};

/** Format heure locale avec secondes : "HH:MM:SS". */
export const formatTimeSecondsFR = (date: Date | null): string => {
  if (!date) return '';
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  const seconds = date.getSeconds().toString().padStart(2, '0');
  return `${hours}:${minutes}:${seconds}`;
};

export const formatDateTimeFR = (date: Date | null): string => {
  if (!date) return '';
  return `${formatDateFR(date)} ${formatTimeFR(date)}`;
};

/**
 * Format relatif adapte pour du monitoring live (derniere activite,
 * derniere synchro, etc.). Logique :
 * - < 60s -> "Xs"
 * - < 1h  -> "Xm Ys"
 * - meme jour -> "HH:MM:SS"
 * - sinon -> "DD/MM HH:MM"
 *
 * Retourne `{ text, tone }` ou `tone` suggere une couleur (recent/default/muted).
 */
export interface RelativeTimeFormat {
  text: string;
  tone: 'muted' | 'recent' | 'default';
}

export const formatRelativeTimeFR = (
  iso: string | Date | null,
  now: Date = new Date()
): RelativeTimeFormat => {
  if (!iso) return { text: 'Jamais', tone: 'muted' };
  const date = typeof iso === 'string' ? new Date(iso) : iso;
  const diffSec = Math.floor((now.getTime() - date.getTime()) / 1000);
  if (diffSec < 0) {
    // Clock skew : on affiche juste l'heure locale avec secondes.
    return { text: formatTimeSecondsFR(date), tone: 'recent' };
  }
  if (diffSec < 60) {
    return { text: `${diffSec}s`, tone: 'recent' };
  }
  if (diffSec < 3600) {
    const m = Math.floor(diffSec / 60);
    const s = diffSec % 60;
    return { text: `${m}m ${s}s`, tone: 'default' };
  }
  const sameDay = date.toDateString() === now.toDateString();
  if (sameDay) {
    return { text: formatTimeSecondsFR(date), tone: 'default' };
  }
  return { text: `${formatDateFR(date)} ${formatTimeFR(date)}`, tone: 'default' };
};

/**
 * `reference` : jour civil servant d'"aujourd'hui" (champs locaux). Par
 * defaut le jour de la machine ; le DatePicker passe le jour du site quand il
 * recoit un fuseau.
 */
export const getDefaultShortcuts = (reference: Date = new Date()): DatePickerShortcut[] => {
  const today = new Date(reference.getFullYear(), reference.getMonth(), reference.getDate());

  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  const last7Days = new Date(today);
  last7Days.setDate(last7Days.getDate() - 6);

  const last30Days = new Date(today);
  last30Days.setDate(last30Days.getDate() - 29);

  const thisMonthStart = new Date(today.getFullYear(), today.getMonth(), 1);
  const lastMonthStart = new Date(today.getFullYear(), today.getMonth() - 1, 1);
  const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);

  return [
    { label: "Aujourd'hui", start: today, end: today },
    { label: 'Hier', start: yesterday, end: yesterday },
    { label: 'Les 7 derniers jours', start: last7Days, end: today },
    { label: 'Les 30 derniers jours', start: last30Days, end: today },
    { label: 'Ce mois-ci', start: thisMonthStart, end: today },
    { label: 'Le mois dernier', start: lastMonthStart, end: lastMonthEnd },
  ];
};

export const getDaysInMonth = (year: number, month: number): Date[] => {
  const days: Date[] = [];
  const date = new Date(year, month, 1);
  while (date.getMonth() === month) {
    days.push(new Date(date));
    date.setDate(date.getDate() + 1);
  }
  return days;
};

export const getCalendarDays = (year: number, month: number): (Date | null)[] => {
  const days = getDaysInMonth(year, month);
  const firstDayOfWeek = days[0].getDay();
  const startPadding = firstDayOfWeek === 0 ? 6 : firstDayOfWeek - 1;

  const prevMonthDays: (Date | null)[] = [];
  if (startPadding > 0) {
    const prevMonth = month === 0 ? 11 : month - 1;
    const prevYear = month === 0 ? year - 1 : year;
    const daysInPrevMonth = new Date(prevYear, prevMonth + 1, 0).getDate();
    for (let i = startPadding - 1; i >= 0; i--) {
      prevMonthDays.push(new Date(prevYear, prevMonth, daysInPrevMonth - i));
    }
  }

  const totalCells = 42;
  const currentDays = [...prevMonthDays, ...days];
  const nextMonthDays: Date[] = [];
  const remaining = totalCells - currentDays.length;
  if (remaining > 0) {
    const nextMonth = month === 11 ? 0 : month + 1;
    const nextYear = month === 11 ? year + 1 : year;
    for (let i = 1; i <= remaining; i++) {
      nextMonthDays.push(new Date(nextYear, nextMonth, i));
    }
  }

  return [...currentDays, ...nextMonthDays];
};

export const isSameDay = (a: Date | null, b: Date | null): boolean => {
  if (!a || !b) return false;
  return (
    a.getDate() === b.getDate() &&
    a.getMonth() === b.getMonth() &&
    a.getFullYear() === b.getFullYear()
  );
};

export const isInRange = (date: Date, start: Date | null, end: Date | null): boolean => {
  if (!start || !end) return false;
  const time = new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
  const startTime = new Date(start.getFullYear(), start.getMonth(), start.getDate()).getTime();
  const endTime = new Date(end.getFullYear(), end.getMonth(), end.getDate()).getTime();
  return time >= startTime && time <= endTime;
};

export const isDateDisabled = (date: Date, minDate?: Date, maxDate?: Date): boolean => {
  const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
  if (minDate) {
    const minOnly = new Date(minDate.getFullYear(), minDate.getMonth(), minDate.getDate()).getTime();
    if (dateOnly < minOnly) return true;
  }
  if (maxDate) {
    const maxOnly = new Date(maxDate.getFullYear(), maxDate.getMonth(), maxDate.getDate()).getTime();
    if (dateOnly > maxOnly) return true;
  }
  return false;
};

export const isMonthFullyDisabled = (
  year: number,
  month: number,
  minDate?: Date,
  maxDate?: Date
): boolean => {
  if (minDate) {
    const lastDay = new Date(year, month + 1, 0);
    const minOnly = new Date(minDate.getFullYear(), minDate.getMonth(), minDate.getDate());
    if (lastDay < minOnly) return true;
  }
  if (maxDate) {
    const firstDay = new Date(year, month, 1);
    const maxOnly = new Date(maxDate.getFullYear(), maxDate.getMonth(), maxDate.getDate());
    if (firstDay > maxOnly) return true;
  }
  return false;
};

export const isShortcutDisabled = (
  shortcut: DatePickerShortcut,
  minDate?: Date,
  maxDate?: Date
): boolean => {
  if (minDate && isDateDisabled(shortcut.start, minDate, undefined)) return true;
  if (maxDate && isDateDisabled(shortcut.end, undefined, maxDate)) return true;
  return false;
};

// =============================================================================
// URL PARAM SERIALIZATION (Date <-> string for query params)
// =============================================================================

/**
 * Parse a URL date param (YYYY-MM-DD or YYYY-MM-DDTHH:mm) to a local Date object.
 */
export const parseDateParam = (dateStr: string | null): Date | null => {
  if (!dateStr) return null;
  if (dateStr.includes('T')) {
    const [datePart, timePart] = dateStr.split('T');
    const [year, month, day] = datePart.split('-').map(Number);
    const [hours, minutes] = timePart.split(':').map(Number);
    return new Date(year, month - 1, day, hours, minutes);
  }
  const [year, month, day] = dateStr.split('-').map(Number);
  return new Date(year, month - 1, day);
};

/**
 * Format a Date to a URL date param string.
 * Returns YYYY-MM-DD if time is midnight, YYYY-MM-DDTHH:mm otherwise.
 */
export const formatDateParam = (date: Date | null): string | null => {
  if (!date) return null;
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  const hours = date.getHours();
  const minutes = date.getMinutes();
  if (hours === 0 && minutes === 0) {
    return `${year}-${month}-${day}`;
  }
  return `${year}-${month}-${day}T${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
};

/**
 * Format a URL date param (YYYY-MM-DD or YYYY-MM-DDTHH:mm) for display in FR.
 * Returns "DD/MM/YYYY" or "DD/MM/YYYY a HHhmm".
 */
export const formatDateParamDisplay = (dateStr: string | null): string => {
  if (!dateStr) return '';
  if (dateStr.includes('T')) {
    const [datePart, timePart] = dateStr.split('T');
    const [year, month, day] = datePart.split('-');
    const [hours, minutes] = timePart.split(':');
    return `${day}/${month}/${year} a ${hours}h${minutes}`;
  }
  const [year, month, day] = dateStr.split('-');
  return `${day}/${month}/${year}`;
};

/**
 * Check if a date param string (YYYY-MM-DD or YYYY-MM-DDTHH:mm) represents today.
 * Returns true for null (means "current/today").
 */
export const isDateParamToday = (dateStr: string | null): boolean => {
  if (!dateStr) return true;
  const now = new Date();
  const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  const datePart = dateStr.includes('T') ? dateStr.split('T')[0] : dateStr;
  return datePart === today;
};

/**
 * Check if a date param string includes a time component.
 */
export const dateParamHasTime = (dateStr: string | null): boolean => {
  return dateStr !== null && dateStr.includes('T');
};

export const clampTime = (value: number, min: number, max: number): number => {
  return Math.max(min, Math.min(max, value));
};

export const applyTimeToDate = (
  date: Date | null,
  hours: number,
  minutes: number
): Date | null => {
  if (!date) return null;
  const d = new Date(date);
  d.setHours(hours, minutes, 0, 0);
  return d;
};
